import React, { useMemo, useCallback } from 'react';
import { SelectField as Select } from '@oneaudi/unified-web-components';
import { toInteger } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Filter, FilterFrame, FilterSelection } from '../Filters.style';
import { RangeFilterOption } from '../../../types';
import { ExposedFiltersTypes, FilterTypes } from '../../../constants/initialFilters';
import FilterExposedHeader from '../FilterExposedHeader';
import { PriceFilterWrapper } from './PriceSelector.style';
import useAnimations from '../../../hooks/Animations/useAnimations';

interface PriceSelectorTypes {
  filterType: string;
  confirmedFilters: FilterTypes;
  filterOptions: {
    min: { [key: string]: RangeFilterOption };
    max: { [key: string]: RangeFilterOption };
  };
  openedFiltersList: ExposedFiltersTypes;
  handleChangeRange: (filterType: string, filterValue: number[]) => void;
  handleExposeFilter: (arg: string) => void;
}

const PriceSelector: React.FC<PriceSelectorTypes> = ({
  filterType,
  confirmedFilters,
  handleChangeRange,
  handleExposeFilter,
  filterOptions,
  openedFiltersList,
}) => {
  const { t } = useTranslation();

  const selectedRange = useMemo(() => {
    return confirmedFilters.price;
  }, [confirmedFilters.price]);

  const optionDomain = useMemo(() => {
    // Convert the strings to numbers and find the minimum and maximum value
    const minKeys = Object.keys(filterOptions.min);
    const maxKeys = Object.keys(filterOptions.max);
    const min = Math.min(...minKeys.map((item) => parseInt(item, 10)));
    const max = Math.max(...maxKeys.map((item) => parseInt(item, 10)));

    return {
      minOption: min,
      maxOption: max,
    };
  }, [filterOptions]);

  // disable showing label inside select component when there is a value selected bu user
  const RangeHasValue = useMemo(() => {
    return !!selectedRange.length;
  }, [selectedRange]);

  const selectedRangeLabel = useMemo(() => {
    const [minValue, maxValue] = selectedRange;

    if (minValue < 0 && maxValue < 0) return '';

    const formatLabel = (label: 'min' | 'max', value: number) => {
      return value >= 0 ? `${label}: ${filterOptions[label][value]?.label}` : '';
    };

    const minLabel = formatLabel('min', minValue);
    const maxLabel = formatLabel('max', maxValue);

    return `${minLabel}${minLabel && maxLabel ? ', ' : ''}${maxLabel}`;
  }, [filterOptions, selectedRange]);

  // Handling changes to the mileage range (min or max) based on user selection
  const handleChangeValue = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>, isMin: boolean) => {
      // Parsing the input value to an integer, providing a default value of -1 if it's not a valid integer
      const newInputValue = Number.isNaN(event.target.value) ? -1 : toInteger(event.target.value);
      const [minValue, maxValue] = selectedRange;

      const newValue = isMin
        ? [newInputValue, maxValue || optionDomain.maxOption]
        : [minValue || optionDomain.minOption, newInputValue];

      handleChangeRange(filterType, newValue);
    },
    [filterType, handleChangeRange, optionDomain.maxOption, optionDomain.minOption, selectedRange],
  );

  const renderOptions = useCallback(
    (options: { [key: string]: RangeFilterOption }, isMin: boolean) => {
      return Object.keys(options).map((filterKey) => {
        const { ariaLabel, label, value } = options[filterKey];
        return (
          <option
            data-testid={label}
            key={ariaLabel}
            aria-label={ariaLabel}
            value={value.toString()}
            disabled={
              (isMin && selectedRange.length > 1 && value >= selectedRange[1]) ||
              (!isMin && selectedRange.length > 0 && value <= selectedRange[0])
            }
          >
            {label}
          </option>
        );
      });
    },
    [selectedRange],
  );

  const showFilter = openedFiltersList[filterType];

  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(showFilter);

  return (
    <Filter>
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedRangeLabel}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props} data-testid="price-filter">
              <FilterFrame style={filtersAccordianAnimation}>
                <PriceFilterWrapper>
                  <Select
                    id={`select_min_${filterType}`}
                    label={t(`vlp.filters.filterOptions.${filterType}.minLabel`)}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      handleChangeValue(e, true)
                    }
                    value={selectedRange[0]?.toString() || ''}
                    // hideLabelOptional
                    className={RangeHasValue ? 'hasValue' : ''}
                    required
                    data-testid="select-min-price"
                  >
                    <option
                      data-testid="select-an-option"
                      key="select-an-option"
                      aria-label={t(`vlp.filters.filterOptions.${filterType}.minLabel`)}
                      value=""
                    >
                      {t(`vlp.filters.filterOptions.${filterType}.minLabel`)}
                    </option>
                    {renderOptions(filterOptions.min, true)}
                  </Select>
                  <Select
                    id={`select_max_${filterType}`}
                    label={t(`vlp.filters.filterOptions.${filterType}.maxLabel`)}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                      handleChangeValue(e, false)
                    }
                    value={selectedRange[1]?.toString() || ''}
                    // hideLabelOptional
                    className={RangeHasValue ? 'hasValue' : ''}
                    required
                    data-testid="select-max-price"
                  >
                    <option
                      data-testid="select-an-option"
                      key="select-an-option"
                      aria-label={t(`vlp.filters.filterOptions.${filterType}.maxLabel`)}
                      value=""
                    >
                      {t(`vlp.filters.filterOptions.${filterType}.maxLabel`)}
                    </option>
                    {renderOptions(filterOptions.max, false)}
                  </Select>
                </PriceFilterWrapper>
              </FilterFrame>
            </FilterSelection>
          ),
      )}
    </Filter>
  );
};

export default PriceSelector;
